import { Download, FileQuestion, Upload } from 'lucide-react';
import { useCallback, useState } from 'react';

import { Button } from '../../../../components/Button';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { FileField, USER_DOCUMENT_MAX_BYTES } from '../../../../components/inputs/FileField';
import { useBlobPreview } from '../../../../hooks/useBlobPreview';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { formatDateTime } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evaluationsApi } from '../praiseApi';
import { AWARD_STAGE, type AwardDetail, type AwardStageDocument, type AwardStageValue } from '../praiseTypes';

/** Each stage's name, as the two modals say it. */
const STAGE_NAME: Record<AwardStageValue, string> = {
  [AWARD_STAGE.Deliberation]: 'Deliberation',
  [AWARD_STAGE.FinalAwarding]: 'Final awarding',
};

/**
 * Attaches one file to a stage. **No Autogenerate**: the one field is a binary whose format the server
 * verifies by magic bytes — the stated exemption.
 */
export function UploadStageDocumentModal({
  award,
  stage,
  onClose,
  onSaved,
}: {
  award: AwardDetail;
  stage: AwardStageValue;
  onClose: () => void;
  onSaved: (next: AwardDetail) => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const file = files[0] ?? null;
  const tooLarge = file !== null && file.size > USER_DOCUMENT_MAX_BYTES;

  async function submit() {
    if (file === null || tooLarge) return;

    setSaving(true);
    setProgress(0);
    setError(null);

    try {
      onSaved(await evaluationsApi.uploadStageDocument(award.id, stage, file, setProgress));
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not attach that document.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'award-stage-document' });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Attach a ${STAGE_NAME[stage].toLowerCase()} document`}
      size="md"
      id="award-stage-document-upload-modal"
      confirm={{ id: 'award-stage-document-upload-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="award-stage-document-upload-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            id="award-stage-document-upload-submit"
            onClick={() => void submit()}
            loading={saving}
            loadingLabel={progress === null ? 'Submitting' : `Filing ${progress}%`}
            disabled={file === null || tooLarge || !confirmed}
            leading={<Upload className="h-4 w-4" aria-hidden="true" />}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error.message}
          </p>
        ) : null}

        <p className="text-sm text-slate-600">
          Attached to the {STAGE_NAME[stage].toLowerCase()} of{' '}
          <span className="font-medium text-slate-800">{award.categoryName}</span> ({award.referenceNumber}).
        </p>

        <FileField
          id="award-stage-document-file"
          label="Document"
          description={
            `The scanned document — PDF, PNG or JPEG, up to ${formatBytes(USER_DOCUMENT_MAX_BYTES)}. ` +
            "The type is verified against the file's own leading bytes rather than its name."
          }
          files={files}
          onChange={setFiles}
          required
          disabled={saving}
          error={error?.fieldError('file') ?? undefined}
        />
      </div>
    </Modal>
  );
}

/** One stage file at the size the screen allows, beside its facts — `useBlobPreview` owns everything security-bearing. */
export function StageDocumentPreviewModal({
  award,
  document,
  onDownload,
  onClose,
}: {
  award: AwardDetail;
  document: AwardStageDocument;
  onDownload: () => void;
  onClose: () => void;
}) {
  const load = useCallback(
    (signal: AbortSignal) => evaluationsApi.downloadStageDocument(award.id, document.id, signal),
    [award.id, document.id],
  );

  const { kind, url, loading, error } = useBlobPreview(document.id, document.contentType ?? null, load);

  return (
    <Modal
      open
      onClose={onClose}
      title={document.fileName}
      size="3xl"
      id="award-stage-document-preview-modal"
      footer={
        <Button
          id="award-stage-document-preview-download"
          variant="secondary"
          leading={<Download className="h-4 w-4" aria-hidden="true" />}
          onClick={onDownload}
          tabIndex={0}
        >
          Download
        </Button>
      }
    >
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          {kind === 'none' ? (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 rounded-lg bg-slate-50">
              <FileQuestion className="h-10 w-10 text-slate-300" aria-hidden="true" />
              <EmptyState minHeight="min-h-0" message="This file cannot be shown here. Download it to open it." />
            </div>
          ) : loading ? (
            <Skeleton className="h-[55vh] w-full" />
          ) : error || !url ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-lg bg-slate-50">
              <ErrorState message={error?.message ?? 'Could not load the preview.'} />
            </div>
          ) : kind === 'image' ? (
            <div className="flex items-center justify-center rounded-lg bg-slate-100 p-3">
              <img src={url} alt={document.fileName} className="max-h-[55vh] w-auto max-w-full rounded object-contain" />
            </div>
          ) : (
            <iframe src={url} title={`Preview of ${document.fileName}`} className="h-[55vh] w-full rounded-lg border-0 bg-slate-50" />
          )}
        </div>

        <div className="w-full shrink-0 lg:w-80">
          <PreviewTable
            rows={[
              { label: 'Stage', value: STAGE_NAME[document.stage] },
              { label: 'Type', value: document.contentType ?? '—', mono: document.contentType != null },
              { label: 'Size', value: document.sizeBytes != null ? formatBytes(document.sizeBytes) : '—', mono: document.sizeBytes != null },
              { label: 'Attached at', value: formatDateTime(document.createdAt), mono: true },
              { label: 'Attached by', value: document.createdByName ?? '—' },
            ]}
          />
        </div>
      </div>
    </Modal>
  );
}
